/* ============================================================================
   camados-anleitung.js — die Aufbau-Anleitung blättert beim Scrollen.

   Lars' Idee (25.09.2026): Wie die App auf der Software-Seite („Sieh, was deine
   Boxen tun"), nur für den Zusammenbau: Eine Bildfolge aus Blender (90 Bilder,
   Renderings nach den echten Druckteilen) läuft beim Scrollen ab — die Kugel
   ohne Deckel, der Tieftöner fährt von vorn in die Senke, sechs Schrauben ziehen
   ihn fest. Daneben leuchtet der Schritt auf, der gerade passiert.

   ── ZWEI BETRIEBSARTEN (wie camados-app.js) ────────────────────────────────
   * ANGEHEFTET (Bewegung erlaubt, kein Vorfahre mit overflow): Die Bühne bleibt
     stehen, während man weiter scrollt (data-an-strecke, sonst 1400 px breit /
     1000 px schmal); in dieser Strecke läuft die Bildfolge einmal durch.
     Schmal (unter 990 px, Lars 25.09.: beim Handy scrollte man aus dem Bild):
     Bild oben in voller Breite, darunter nur der Schritt, der gerade läuft —
     die Schritte blenden über (an--schmal).
   * FREI (sonst): Die Bühne scrollt mit; die Bildfolge läuft ab, während sie
     durchs Bild wandert.

   ── WAS NIE PASSIERT ───────────────────────────────────────────────────────
   Ohne Skript steht das letzte Bild (alles montiert) im <img>, und die drei
   Schritte stehen lesbar daneben. Mit `prefers-reduced-motion` bleibt es dabei;
   nur der Schritt, der zur Scrollposition gehört, wird hervorgehoben. Bilder
   werden erst geladen, wenn der Abschnitt in die Nähe kommt — 90 Bilder à
   ~40 KB sind sonst 3,5 MB, die niemand sehen wollte.
   ========================================================================== */

(function () {
  'use strict';

  var SANFT = window.matchMedia('(prefers-reduced-motion: reduce)');
  var BREIT = window.matchMedia('(min-width: 990px)');
  var MIN_BILD_SCHMAL = 200;   // px: weniger Bild lohnt das Anheften auf dem Handy nicht

  function klemmen(x) { return x < 0 ? 0 : x > 1 ? 1 : x; }

  function kopfHoehe() {
    return parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
  }

  function darfAnheften(wurzel) {
    if (SANFT.matches) return false;
    if (!window.CSS || !CSS.supports('position', 'sticky')) return false;
    for (var el = wurzel.parentElement; el && el !== document.body; el = el.parentElement) {
      var s = getComputedStyle(el);
      if (/hidden|auto|scroll/.test(s.overflowX + ' ' + s.overflowY)) return false;
    }
    return true;
  }

  function einrichten(wurzel) {
    if (wurzel.hasAttribute('data-an-bereit')) return;
    wurzel.setAttribute('data-an-bereit', '');
    // Der umgebende Theme-Block zieht sich sonst auf die Inhaltsbreite zusammen (siehe camados-anleitung.css).
    if (wurzel.parentElement) {
      wurzel.parentElement.style.setProperty('width', '100%');
      wurzel.parentElement.style.setProperty('align-self', 'stretch');
    }

    var leinwand = wurzel.querySelector('[data-an-leinwand]');
    var standbild = wurzel.querySelector('[data-an-standbild]');
    var schritte = [].slice.call(wurzel.querySelectorAll('[data-an-schritt]'));
    var quellen;
    try { quellen = JSON.parse(wurzel.getAttribute('data-an-bilder') || '[]'); } catch (e) { quellen = []; }
    if (!leinwand || !quellen.length || !leinwand.getContext) return;

    // Die Phasen stehen im Markup (data-an-von / data-an-bis als Bildnummern), damit Text und
    // Bild aus derselben Quelle kommen (werkzeug/seite-anleitung-bauen.py).
    var phasen = schritte.map(function (li) {
      return { von: parseInt(li.getAttribute('data-an-von'), 10) || 1,
               bis: parseInt(li.getAttribute('data-an-bis'), 10) || quellen.length };
    });

    var ctx = leinwand.getContext('2d');
    var bilder = new Array(quellen.length);
    var geladen = 0;
    var angeheftet = false;
    var strecke = 1400;
    var sichtbar = false;
    var geplant = false;
    var letztesBild = -1;
    var aktiverSchritt = -1;
    var letzterFortschritt = -1;
    var ladenGestartet = false;
    // Vorab entpackte Bilder (Lars 25.09.: „ein bisschen laggt die Animation"): Ein WebP 1200×900 mit
    // Transparenz kostet beim ersten Zeichnen ~14 ms Entpacken auf dem Hauptthread — genau beim Scrollen.
    // Deshalb werden die Bilder um die aktuelle Stelle herum im Hintergrund in ImageBitmaps gewandelt
    // (in der gezeigten Größe, nie größer als das Bild); Zeichnen kostet dann ~0,5 ms. Speicher bleibt
    // klein, weil nur ein Fenster um die aktuelle Stelle gehalten wird (breit ±20, schmal ±12 Bilder).
    var KANN_BITMAP = typeof window.createImageBitmap === 'function';
    var bitmaps = new Array(quellen.length);   // ImageBitmap | false (in Arbeit) | null (geht nicht) | undefined
    var bitmapBreite = 0;                       // Zielbreite in Gerätepixeln
    var dekodierLaufend = 0;
    var zielIndex = 0;

    function laden() {
      if (ladenGestartet) return;
      ladenGestartet = true;
      // Erst jedes 6. Bild (grobe Folge sofort), dann der Rest — so ist schon beim ersten
      // Scrollen etwas zu sehen, ohne dass 90 Anfragen gleichzeitig starten.
      var reihenfolge = [];
      for (var i = 0; i < quellen.length; i += 6) reihenfolge.push(i);
      for (var j = 0; j < quellen.length; j++) if (j % 6) reihenfolge.push(j);
      var laufend = 0, naechster = 0;
      function weiter() {
        while (laufend < 4 && naechster < reihenfolge.length) {
          (function (idx) {
            var img = new Image();
            img.decoding = 'async';
            img.onload = img.onerror = function () {
              laufend--; geladen++;
              if (img.naturalWidth) bilder[idx] = img;
              planen(); weiter();
            };
            img.src = quellen[idx];
            laufend++;
          })(reihenfolge[naechster++]);
        }
      }
      weiter();
    }

    function messen() {
      angeheftet = darfAnheften(wurzel);
      var schmal = !BREIT.matches;
      wurzel.classList.toggle('an--gepinnt', angeheftet);
      wurzel.classList.toggle('an--schmal', schmal);
      wurzel.style.removeProperty('--an-hoehe');
      wurzel.style.removeProperty('--an-schritt-hoehe');
      strecke = parseInt(wurzel.getAttribute('data-an-strecke'), 10) || (schmal ? 1000 : 1400);
      wurzel.style.setProperty('--an-strecke', strecke + 'px');
      if (angeheftet) {
        // Die Bühne muss unter den Kopfbereich passen — sonst ragt sie oben heraus.
        var frei = window.innerHeight - kopfHoehe() - 48;
        if (schmal) {
          // Bild in voller Breite, darunter der laufende Schritt; die Schritte liegen übereinander,
          // der Kasten bekommt die Höhe des längsten. Das Bildformat (Höhe durch Breite) steht in
          // data-an-format, Standard 4:3; die Duet im Hochformat auf der Startseite hat 1,25 (27.09.2026).
          var schrittHoehe = 0;
          schritte.forEach(function (li) { schrittHoehe = Math.max(schrittHoehe, li.offsetHeight); });
          var format = parseFloat(wurzel.getAttribute('data-an-format')) || 0.75;
          var bildHoehe = Math.round(wurzel.clientWidth * format);
          var hoehe = Math.min(frei, bildHoehe + 16 + schrittHoehe);
          if (hoehe - schrittHoehe - 16 < MIN_BILD_SCHMAL) {
            // Kleiner Bildschirm, langer Text: Angeheftet bliebe vom Bild nur ein Streifen. Dann lieber
            // frei laufen lassen (Bild oben, alle Schritte darunter), das ist nie schlechter als gar kein Effekt.
            angeheftet = false;
            wurzel.classList.remove('an--gepinnt');
            wurzel.style.removeProperty('--an-strecke');
          } else {
            wurzel.style.setProperty('--an-schritt-hoehe', schrittHoehe + 'px');
            wurzel.style.setProperty('--an-hoehe', hoehe + 'px');
          }
        } else {
          wurzel.style.setProperty('--an-hoehe', Math.max(360, Math.min(720, frei)) + 'px');
        }
      }
      bitmapBreiteBestimmen();
    }

    function bitmapBreiteBestimmen() {
      if (!KANN_BITMAP) return;
      var css = leinwand.getBoundingClientRect().width || wurzel.clientWidth || 0;
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      var neu = Math.max(320, Math.round(css * dpr));
      if (neu === bitmapBreite) return;
      bitmapBreite = neu;
      for (var i = 0; i < bitmaps.length; i++) {
        if (bitmaps[i] && bitmaps[i].close) bitmaps[i].close();
        if (bitmaps[i] !== false) bitmaps[i] = undefined;   // laufende Entpackungen prüfen ihre Breite selbst
      }
      letztesBild = -1;
    }

    function fenster() { return BREIT.matches ? 20 : 12; }

    function dekodierenUm(ziel) {
      if (!KANN_BITMAP || !bitmapBreite) return;
      zielIndex = ziel;
      var f = fenster();
      for (var i = 0; i < bitmaps.length; i++) {
        if (bitmaps[i] && Math.abs(i - ziel) > f + 10) { bitmaps[i].close(); bitmaps[i] = undefined; }
      }
      // Fehlende Bilder im Fenster entpacken, nahe zuerst, höchstens zwei zugleich.
      for (var d = 0; d <= f && dekodierLaufend < 2; d++) {
        var kandidaten = d === 0 ? [ziel] : [ziel + d, ziel - d];
        for (var k = 0; k < kandidaten.length && dekodierLaufend < 2; k++) {
          var idx = kandidaten[k];
          if (idx >= 0 && idx < bitmaps.length && bitmaps[idx] === undefined && bilder[idx]) dekodieren(idx);
        }
      }
    }

    function dekodieren(idx) {
      var img = bilder[idx];
      var breite = Math.min(img.naturalWidth, bitmapBreite);
      var hoehe = Math.round(img.naturalHeight * breite / img.naturalWidth);
      var erwartet = bitmapBreite;
      bitmaps[idx] = false;
      dekodierLaufend++;
      var versprechen;
      try {
        versprechen = breite < img.naturalWidth
          ? window.createImageBitmap(img, { resizeWidth: breite, resizeHeight: hoehe, resizeQuality: 'medium' })
          : window.createImageBitmap(img);
      } catch (e) {
        versprechen = Promise.reject(e);
      }
      versprechen.then(function (bm) {
        dekodierLaufend--;
        if (bitmapBreite !== erwartet) { bm.close(); bitmaps[idx] = undefined; }
        else { bitmaps[idx] = bm; if (idx === zielIndex) { letztesBild = -1; planen(); } }
        dekodierenUm(zielIndex);
      }, function () {
        dekodierLaufend--;
        bitmaps[idx] = null;   // geht nicht → das Bild selbst zeichnen
        dekodierenUm(zielIndex);
      });
    }

    function naechstesFertige(idx) {
      // Nahe liegendes, schon entpacktes Bild (bis 6 Bilder daneben) — besser als jetzt synchron entpacken.
      for (var d = 0; d <= 6; d++) {
        if (idx - d >= 0 && bitmaps[idx - d]) return idx - d;
        if (idx + d < quellen.length && bitmaps[idx + d]) return idx + d;
      }
      return -1;
    }

    function fortschritt() {
      var h = window.innerHeight;
      if (angeheftet) {
        // Die Bühne klebt bei Kopfhöhe + 24 px (camados-anleitung.css) und bleibt `strecke` px dort stehen.
        var r = wurzel.getBoundingClientRect();
        return klemmen((kopfHoehe() + 24 - r.top) / strecke);
      }
      // Frei: Die Folge läuft, während die Bühne vom unteren Rand bis zur oberen Hälfte wandert.
      var b = leinwand.getBoundingClientRect();
      var nenner = h * 0.5 + b.height;
      var p = (h - b.top) / nenner;
      // Steht die Bühne schon beim Laden im Bild (erster Abschnitt der Seite, etwa die Duet auf der
      // Startseite am Handy), beginnt die Folge trotzdem beim ersten Bild: gezählt wird ab dem Wert, den
      // sie ganz oben auf der Seite hätte (27.09.2026). Weiter unten ist p0 <= 0 und nichts ändert sich.
      var oben = b.top + window.scrollY;
      var p0 = (h - oben) / nenner;
      if (p0 > 0) {
        p = p0 < 0.95 ? (p - p0) / (1 - p0) : window.scrollY / Math.max(1, oben + b.height);
      }
      return klemmen(p);
    }

    function naechstesGeladene(idx) {
      if (bilder[idx]) return idx;
      for (var d = 1; d < quellen.length; d++) {
        if (idx - d >= 0 && bilder[idx - d]) return idx - d;
        if (idx + d < quellen.length && bilder[idx + d]) return idx + d;
      }
      return -1;
    }

    function zeichnen() {
      geplant = false;
      var p = fortschritt();
      var ruhig = SANFT.matches;
      var nr = ruhig ? quellen.length : 1 + Math.round(p * (quellen.length - 1));   // 1-basiert wie die Dateien

      // Fortschritt als CSS-Variable --an-p (0 … 1), damit ein Abschnitt Hintergrund und Texte mitlaufen
      // lassen kann (Startseite in vier Stellen, 27.09.2026: dunkel → hell, während die Duet sich dreht).
      // Bei reduzierter Bewegung setzt das Skript nichts: Dann gilt der Rückfallwert, den der Abschnitt
      // in seinem Stil vorgibt (derselbe wie ohne Skript), passend zu seinem Standbild.
      var pv = ruhig ? -2 : Math.round(p * 1000) / 1000;
      if (pv !== letzterFortschritt) {
        letzterFortschritt = pv;
        if (ruhig) wurzel.style.removeProperty('--an-p');
        else wurzel.style.setProperty('--an-p', String(pv));
      }

      if (!ruhig) {
        var wunsch = nr - 1;
        dekodierenUm(wunsch);
        var idx = naechstesFertige(wunsch);
        var quelle = idx >= 0 ? bitmaps[idx] : null;
        if (idx < 0) { idx = naechstesGeladene(wunsch); quelle = idx >= 0 ? bilder[idx] : null; }
        if (quelle && idx !== letztesBild) {
          letztesBild = idx;
          var w = quelle.naturalWidth || quelle.width, h = quelle.naturalHeight || quelle.height;
          if (leinwand.width !== w || leinwand.height !== h) { leinwand.width = w; leinwand.height = h; }
          ctx.clearRect(0, 0, w, h);
          ctx.drawImage(quelle, 0, 0);
          wurzel.classList.add('an--laeuft');
        }
      }

      var neu = -1;
      for (var k = 0; k < phasen.length; k++) {
        if (nr >= phasen[k].von && nr <= phasen[k].bis) neu = k;
      }
      if (neu !== aktiverSchritt) {
        aktiverSchritt = neu;
        schritte.forEach(function (li, i) { li.classList.toggle('an__schritt--aktiv', i === neu); });
      }
    }

    function planen() {
      if (geplant || !sichtbar) return;
      geplant = true;
      window.requestAnimationFrame(zeichnen);
    }

    function neuMessen() {
      messen();
      geplant = false;
      zeichnen();
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (eintraege) {
        sichtbar = eintraege[0].isIntersecting;
        if (sichtbar) { if (!SANFT.matches) laden(); planen(); }
      }, { rootMargin: '600px 0px' }).observe(wurzel);
    } else {
      sichtbar = true;
      if (!SANFT.matches) laden();
    }

    window.addEventListener('scroll', planen, { passive: true });
    window.addEventListener('resize', neuMessen);
    [SANFT, BREIT].forEach(function (mq) {
      if (mq.addEventListener) mq.addEventListener('change', neuMessen);
    });

    messen();
    zeichnen();
  }

  function start() {
    document.querySelectorAll('[data-an]').forEach(einrichten);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
  document.addEventListener('shopify:section:load', start);
  window.addEventListener('pageshow', function (e) { if (e.persisted) start(); });
})();
